import {
  Accordion,
  AccordionHeader,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AccordionTitle,
  Group,
} from '@apollo/orbit';
import classnames from 'classnames';
import _ from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React, { Fragment, useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router';

import { GraphQLDiffView } from 'src/components/graphQLDiffView/GraphQLDiffView';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { assertUnreachableOrReturnDefault } from 'src/lib/assertUnreachable';
import { tolerantParseSchema } from 'src/lib/chevrotain/tolerantParseSchema';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { catchAllRouteConfig } from 'src/lib/routeConfig/catchAllRoute';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';

import { mapLineNumsToCoordinates } from '../../documentationPage/schemaPage/sdlEditorHelpers';
import { GraphRef } from '../../hooks/useGraphRef';
import { proposalChangesRouteConfig } from '../../routes';
import { getCoordinateType } from '../diffHelpers/getCoordinateType';
import { SummaryItem } from '../DiffSummary';
import { ImplementationGutterIndicator } from '../implementationGutterIndicator/ImplementationGutterIndicator';

import { DetailDiffCommentButton } from './DetailDiffCommentButton';

type CoordinateTarget = 'description' | 'returnType' | 'none';

export const getIdFriendlyCoordinateName = (
  coordinate: string,
  target: CoordinateTarget,
) =>
  // We need to place the markers at the beginning or else the fuzzy ID matching will match the description
  // when trying to link to the coordinate the description is for
  `${target !== 'none' ? `${target}_` : ''}${coordinate.replace('@', '_').replace(/[@\$\.\(\)\:]/g, '')}`;

type CoordinateWithTarget = {
  coordinate: string | undefined;
  target: CoordinateTarget;
};

// Coordinates in the Changes page come back with suffixes that indicate certain targets
// Right now we only have `~description`, but we plan on adding more. These targets likely will only exist
// once at a time. This function parses these out so we can use them for linking and the getIdFriendlyCoordinateName function above
export const splitOutTargetsFromCoordinate = (
  coordinate: string | undefined,
): CoordinateWithTarget => {
  if (!coordinate) {
    return { coordinate: undefined, target: 'none' };
  }

  const targetMarkerIndex = coordinate.lastIndexOf('~');
  const coordinateWithoutTargetMarker =
    targetMarkerIndex === -1
      ? coordinate
      : coordinate.slice(0, targetMarkerIndex);

  const targetMarker =
    targetMarkerIndex === -1 ? '' : coordinate.slice(targetMarkerIndex + 1);
  switch (targetMarker) {
    case 'description':
      return {
        coordinate: coordinateWithoutTargetMarker,
        target: 'description',
      };
    case 'returnType':
      return {
        coordinate: coordinateWithoutTargetMarker,
        target: 'returnType',
      };
    default:
      return { coordinate, target: 'none' };
  }
};

export const DetailDiff = ({
  typeName,
  oldType,
  newType,
  diffGroup,
  diffGroupIsPartiallyImplemented,
  collapsed,
  toggleCollapsed,
  parentComments,
  canMakeComments,
  onMount,
  implementedChanges,
  shouldOnlyShowUnimplementedLines,
  canQueryLaunches,
  sourceGraphVariant,
  graphRef,
}: {
  typeName: string;
  oldType: ReturnType<typeof getCoordinateType>;
  newType: ReturnType<typeof getCoordinateType>;
  diffGroup: GraphQLTypes.FlatDiffItemFragment[];
  diffGroupIsPartiallyImplemented: boolean;
  collapsed: boolean;
  toggleCollapsed: () => void;
  parentComments: GraphQLTypes.ProposalCommentsParentCommentFragment[];
  // whether or not comments are allowed in this view on the schema coordinate display
  canMakeComments: boolean;
  onMount?: () => void;
  implementedChanges: Omit<
    GraphQLTypes.ImplementedChangesFragment['implementedChanges'][0],
    'subgraph'
  >[];
  shouldOnlyShowUnimplementedLines: boolean;
  canQueryLaunches: boolean;
  sourceGraphVariant: string | undefined;
  graphRef: GraphRef;
}) => {
  const history = useHistory();
  const location = useLocation();
  const { linkedCoordinateName } = useRouteParams(
    proposalChangesRouteConfig,
    catchAllRouteConfig,
  );

  const groupName = useMemo(() => {
    const kind = oldType?.kind ?? newType?.kind;

    return kind === 'SchemaDefinition'
      ? 'Schema'
      : kind === 'InputObjectTypeDefinition'
        ? 'Inputs'
        : kind?.replace(/(Type)?Definition$/, 's');
  }, [newType?.kind, oldType?.kind]);

  const { leftLineNumberMap, rightLineNumberMap } = useMemo(() => {
    const { documentNode: leftDocumentNode } = tolerantParseSchema(
      monacoEditor.editor.createModel(oldType?.printed ?? ''),
    );
    const { documentNode: rightDocumentNode } = tolerantParseSchema(
      monacoEditor.editor.createModel(newType?.printed ?? ''),
    );

    // TODO(proposals): if line has parent comment and the coordinate appears twice,
    // we should only show it on the right side.
    return {
      leftLineNumberMap: mapLineNumsToCoordinates({
        typeDefs: leftDocumentNode,
        includeDescriptionLines: true,
        includeReturnTypes: true,
      }),
      rightLineNumberMap: mapLineNumsToCoordinates({
        typeDefs: rightDocumentNode,
        includeDescriptionLines: true,
        includeReturnTypes: true,
      }),
    };
  }, [newType?.printed, oldType?.printed]);

  const selectedLineNumbers = useMemo(() => {
    const leftFindKey = _.findLast(
      Object.entries(leftLineNumberMap),
      ([, coordinates]) =>
        coordinates.some((coordinate) => coordinate === linkedCoordinateName),
    )?.[0];
    const rightFindKey = _.findLast(
      Object.entries(rightLineNumberMap),
      ([, coordinates]) =>
        coordinates.some((coordinate) => coordinate === linkedCoordinateName),
    )?.[0];

    return [
      {
        type: 'original' as const,
        lineNumber: parseInt(leftFindKey ?? '', 10),
      },
      {
        type: 'modified' as const,
        lineNumber: parseInt(rightFindKey ?? '', 10),
      },
    ].filter(({ lineNumber }) => !isNaN(lineNumber));
  }, [leftLineNumberMap, linkedCoordinateName, rightLineNumberMap]);

  const diffGroupSummary = useMemo(() => {
    const finalSummary = diffGroup.reduce(
      (
        summary: {
          description: boolean;
          directive: { added: number; removed: number };
          interface: { added: number; removed: number };
          location: { added: number; removed: number };
          member: { added: number; removed: number };
          repeatable: boolean;
          argument: {
            added: number;
            removed: number;
            changed: number;
          };
          field: {
            added: string[];
            removed: string[];
            changed: string[];
          };
        },
        diffItem,
      ) => {
        switch (diffItem.__typename) {
          case 'FlatDiffAddImplementation':
            summary.interface.added += 1; // eslint-disable-line no-param-reassign
            break;
          case 'FlatDiffRemoveImplementation':
            summary.interface.removed += 1; // eslint-disable-line no-param-reassign
            break;
          case 'FlatDiffAddField':
          case 'FlatDiffAddEnumValue':
            summary.field.added.push(diffItem.coordinate);
            break;
          case 'FlatDiffAddSchemaRootOperation':
            summary.field.added.push(`schema.${diffItem.rootType}`);
            break;
          case 'FlatDiffRemoveEnumValue':
          case 'FlatDiffRemoveField':
            summary.field.removed.push(diffItem.coordinate);
            break;
          case 'FlatDiffRemoveSchemaRootOperation':
            summary.field.removed.push(`schema.${diffItem.rootType}`);
            break;
          case 'FlatDiffAddUnionMember':
            summary.member.added += 1; // eslint-disable-line no-param-reassign
            break;
          case 'FlatDiffRemoveUnionMember':
            summary.member.removed += 1; // eslint-disable-line no-param-reassign
            break;
          case 'FlatDiffAddValidLocation':
            summary.location.added += 1; // eslint-disable-line no-param-reassign
            break;
          case 'FlatDiffRemoveValidLocation':
            summary.location.removed += 1; // eslint-disable-line no-param-reassign
            break;
          case 'FlatDiffAddArgument':
            if (diffItem.coordinate.startsWith('@')) {
              summary.argument.added += 1; // eslint-disable-line no-param-reassign
            } else {
              summary.field.changed.push(diffItem.coordinate);
            }
            break;
          case 'FlatDiffRemoveArgument':
            if (diffItem.coordinate.startsWith('@')) {
              summary.argument.removed += 1; // eslint-disable-line no-param-reassign
            } else {
              summary.field.changed.push(diffItem.coordinate);
            }
            break;
          case 'FlatDiffAddDirectiveUsage':
          case 'FlatDiffAddSchemaDirectiveUsage':
            summary.directive.added += 1; // eslint-disable-line no-param-reassign
            break;
          case 'FlatDiffRemoveDirectiveUsage':
          case 'FlatDiffRemoveSchemaDirectiveUsage':
            summary.directive.removed += 1; // eslint-disable-line no-param-reassign
            break;
          case 'FlatDiffChangeArgumentDefault':
            if (diffItem.coordinate.startsWith('@')) {
              summary.argument.changed += 1; // eslint-disable-line no-param-reassign
            } else {
              summary.field.changed.push(diffItem.coordinate);
            }
            break;
          case 'FlatDiffChangeInputFieldDefault':
            summary.field.changed.push(diffItem.coordinate);
            break;
          case 'FlatDiffChangeSchemaDescription':
            summary.description = true; // eslint-disable-line no-param-reassign
            break;
          case 'FlatDiffChangeDescription':
            if (diffItem.coordinate.match(/^@?[a-z0-9_]+$/i)) {
              summary.description = true; // eslint-disable-line no-param-reassign
            } else if (diffItem.coordinate.startsWith('@')) {
              summary.argument.changed += 1; // eslint-disable-line no-param-reassign
            } else {
              summary.field.changed.push(diffItem.coordinate);
            }
            break;
          case 'FlatDiffChangeDirectiveRepeatable':
            summary.repeatable = true; // eslint-disable-line no-param-reassign
            break;
          case 'FlatDiffAddSchemaDefinition':
          case 'FlatDiffRemoveSchemaDefinition':
          case 'FlatDiffAddObject':
          case 'FlatDiffRemoveObject':
          case 'FlatDiffAddInput':
          case 'FlatDiffRemoveInput':
          case 'FlatDiffAddInterface':
          case 'FlatDiffRemoveInterface':
          case 'FlatDiffAddEnum':
          case 'FlatDiffRemoveEnum':
          case 'FlatDiffAddScalar':
          case 'FlatDiffRemoveScalar':
          case 'FlatDiffAddUnion':
          case 'FlatDiffRemoveUnion':
          case 'FlatDiffAddDirective':
          case 'FlatDiffRemoveDirective':
            break;
          default:
            assertUnreachableOrReturnDefault(diffItem, '');
        }
        return summary;
      },
      {
        repeatable: false,
        description: false,
        directive: { added: 0, removed: 0 },
        interface: { added: 0, removed: 0 },
        location: { added: 0, removed: 0 },
        member: { added: 0, removed: 0 },
        argument: { added: 0, removed: 0, changed: 0 },
        field: { added: [], removed: [], changed: [] },
      },
    );

    const addedFields = _.uniq(
      finalSummary.field.added.map(
        (coordinate) => coordinate.match(/[a-z0-9_]+\.([a-z0-9_]+)/i)?.[1],
      ),
    );
    const removedFields = _.uniq(
      finalSummary.field.removed.map(
        (coordinate) => coordinate.match(/[a-z0-9_]+\.([a-z0-9_]+)/i)?.[1],
      ),
    );
    return Object.entries({
      ...finalSummary,
      field: {
        added: addedFields.length,
        changed: _.uniq(
          finalSummary.field.changed.map(
            (coordinate) => coordinate.match(/[a-z0-9_]+\.([a-z0-9_]+)/i)?.[1],
          ),
        ).filter(
          (changedField) =>
            !addedFields.includes(changedField) &&
            !removedFields.includes(changedField),
        ).length,
        removed: removedFields.length,
      },
    }).filter(([, summary]) => {
      if (typeof summary === 'boolean') {
        return summary;
      } else {
        return Object.entries(summary).some(([, count]) => count > 0);
      }
    });
  }, [diffGroup]);

  const unimplementedLines = useMemo(() => {
    const getLineNumberFromNumberMapIfExists = (
      diffItem: (typeof diffGroup)[number],
      lineNumberMap: Record<number, string[]>,
    ) => {
      if ('coordinate' in diffItem) {
        const lineNumberMaybeAsString = Object.entries(lineNumberMap).find(
          ([, coordinates]) =>
            diffItem.__typename === 'FlatDiffChangeDescription'
              ? coordinates.includes(`${diffItem.coordinate}~description`)
              : coordinates.includes(diffItem.coordinate),
        )?.[0];
        return lineNumberMaybeAsString
          ? parseInt(lineNumberMaybeAsString, 10)
          : undefined;
      }
    };

    if (shouldOnlyShowUnimplementedLines) {
      return diffGroup.flatMap((diffItem) => {
        const maybeLeftLineNumber = getLineNumberFromNumberMapIfExists(
          diffItem,
          leftLineNumberMap,
        );
        const maybeRightLineNumber = getLineNumberFromNumberMapIfExists(
          diffItem,
          rightLineNumberMap,
        );
        return [
          {
            lineNumber: maybeLeftLineNumber,
            type: 'original' as const,
          },
          { lineNumber: maybeRightLineNumber, type: 'modified' as const },
        ].filter(
          (
            lineItem,
          ): lineItem is typeof lineItem & {
            lineNumber: NonNullable<(typeof lineItem)['lineNumber']>;
          } => typeof lineItem.lineNumber === 'number',
        );
      });
    }
  }, [
    shouldOnlyShowUnimplementedLines,
    diffGroup,
    leftLineNumberMap,
    rightLineNumberMap,
  ]);

  const handleCommentButtonClick = useCallback(
    (coordinate: string, commentId?: string) => {
      if (commentId) {
        history.push(
          proposalChangesRouteConfig.locationFrom({
            location,
            patch: {
              linkedCoordinateName: coordinate,
              linkedCommentId: commentId,
              linkedGroupName: groupName,
              selectedRevision: RouteConfig.DELETE,
            },
            fromRouteConfig: proposalChangesRouteConfig,
          }),
        );
      } else {
        history.push(
          proposalChangesRouteConfig.locationFrom({
            location,
            patch: {
              linkedCoordinateName: coordinate,
              linkedCommentId: RouteConfig.DELETE,
              linkedGroupName: groupName,
              selectedRevision: RouteConfig.DELETE,
            },
            fromRouteConfig: proposalChangesRouteConfig,
          }),
        );
      }
    },
    [groupName, history, location],
  );

  const gutterMarkers = useMemo(() => {
    const getImplementedChange = (coordinate: string) => {
      return implementedChanges.find((change) =>
        'coordinate' in change.diffItem
          ? change.diffItem.__typename === 'FlatDiffChangeDescription'
            ? `${change.diffItem.coordinate}~description` === coordinate
            : change.diffItem.coordinate === coordinate
          : false,
      );
    };

    const leftGutterMarkers = Object.entries(leftLineNumberMap).map(
      ([lineNumberKey, coordinates]) => {
        const lineNumber = parseInt(lineNumberKey, 10);

        // We should show the first valid implementedChange if it exists
        const coordinateWithChanges = coordinates.find((coordinate) =>
          getImplementedChange(coordinate),
        );
        const implementedChange = coordinateWithChanges
          ? getImplementedChange(coordinateWithChanges)
          : undefined;

        // Descriptions may be on multiple lines. We should only place a pin on the first one
        const shouldShowPinForThreadIfExists = !leftLineNumberMap[
          lineNumber - 1
        ]?.some((prevLineCoordinate) =>
          coordinates.includes(prevLineCoordinate),
        );

        return {
          lineNumber,
          type: 'original' as const,
          marker: (
            <div className="flex">
              <ImplementationGutterIndicator
                diffGroupIsPartiallyImplemented={
                  diffGroupIsPartiallyImplemented
                }
                canQueryLaunches={canQueryLaunches}
                graphRef={graphRef}
                implementedChange={implementedChange}
                sourceGraphVariant={sourceGraphVariant}
              />
              {canMakeComments && (
                <DetailDiffCommentButton
                  coordinates={coordinates}
                  parentComments={parentComments}
                  shouldShowPinForThreadIfExists={
                    shouldShowPinForThreadIfExists
                  }
                  handleCommentButtonClick={handleCommentButtonClick}
                  // 🚨 Do not delete or change this data-testid / this button without updating the Proposals Datadog Browser test
                  // See test here: https://app.datadoghq.com/synthetics/details/btp-g22-4vt
                  testId={`proposals_add_comment_button_left_${coordinates[0]}`}
                />
              )}
            </div>
          ),
        };
      },
    );

    const rightGutterMarkers = Object.entries(rightLineNumberMap).map(
      ([lineNumberKey, coordinates]) => {
        const lineNumber = parseInt(lineNumberKey, 10);

        // We should show the first valid implementedChange if it exists
        const coordinateWithChanges = coordinates.find((coordinate) =>
          getImplementedChange(coordinate),
        );
        const implementedChange = coordinateWithChanges
          ? getImplementedChange(coordinateWithChanges)
          : undefined;

        // Descriptions may be on multiple lines. We should only place a pin on the first one
        const shouldShowPinForThreadIfExists = !rightLineNumberMap[
          lineNumber - 1
        ]?.some((prevLineCoordinate) =>
          coordinates.includes(prevLineCoordinate),
        );

        return {
          lineNumber,
          type: 'modified' as const,
          marker: (
            <div className="flex">
              <ImplementationGutterIndicator
                diffGroupIsPartiallyImplemented={
                  diffGroupIsPartiallyImplemented
                }
                canQueryLaunches={canQueryLaunches}
                graphRef={graphRef}
                implementedChange={implementedChange}
                sourceGraphVariant={sourceGraphVariant}
              />
              {canMakeComments && (
                <DetailDiffCommentButton
                  coordinates={coordinates}
                  parentComments={parentComments}
                  shouldShowPinForThreadIfExists={
                    shouldShowPinForThreadIfExists
                  }
                  handleCommentButtonClick={handleCommentButtonClick}
                  // 🚨 Do not delete or change this data-testid / this button without updating the Proposals Datadog Browser test
                  // See test here: https://app.datadoghq.com/synthetics/details/btp-g22-4vt
                  testId={`proposals_add_comment_button_right_${coordinates[0]}`}
                />
              )}
            </div>
          ),
        };
      },
    );

    return [...leftGutterMarkers, ...rightGutterMarkers];
  }, [
    canMakeComments,
    canQueryLaunches,
    diffGroupIsPartiallyImplemented,
    graphRef,
    handleCommentButtonClick,
    implementedChanges,
    leftLineNumberMap,
    parentComments,
    rightLineNumberMap,
    sourceGraphVariant,
  ]);

  return (
    <Accordion
      className="overflow-hidden"
      allowToggle
      index={collapsed ? [] : [0]}
      onChange={() => toggleCollapsed()}
    >
      <AccordionItem>
        <AccordionHeader>
          <Group>
            <AccordionTitle>{typeName}</AccordionTitle>
            {diffGroupSummary.length > 0 && (
              <span className="text-secondary">
                (
                {diffGroupSummary.map(([cat, summary], catIndex) => {
                  if (typeof summary === 'boolean') {
                    return (
                      <Fragment key={cat}>
                        {catIndex > 0 ? '; ' : ''}
                        <SummaryItem
                          className="pr-1"
                          action="changed"
                          count={null}
                        />
                        {format.pluralize(1, cat)}
                      </Fragment>
                    );
                  }
                  const summaryEntries = Object.entries(summary) as Array<
                    [
                      keyof typeof summary,
                      (typeof summary)[keyof typeof summary],
                    ]
                  >;
                  return (
                    <Fragment key={cat}>
                      {summaryEntries
                        .filter(([, count]) => count > 0)
                        .map(([action, count], summaryIndex) => (
                          <Fragment key={action}>
                            {catIndex + summaryIndex > 0 ? '; ' : ''}
                            <SummaryItem
                              className="pr-1"
                              action={action}
                              count={count}
                            />
                            {format.pluralize(count, cat)}
                          </Fragment>
                        ))}
                    </Fragment>
                  );
                })}
                )
              </span>
            )}
          </Group>
          <AccordionIcon />
        </AccordionHeader>
        <AccordionPanel className="px-0">
          <GraphQLDiffView
            oldSdl={oldType?.printed}
            newSdl={newType?.printed}
            className="w-full"
            onMount={onMount}
            lineVisibilityType={
              shouldOnlyShowUnimplementedLines ? 'custom' : 'only-changes'
            }
            customLinesToShow={unimplementedLines}
            additionalVisibleLines={selectedLineNumbers}
            gutterMarkers={gutterMarkers ?? []}
            {...(isMinLengthArray(1, gutterMarkers)
              ? {
                  lineNumberClassName: '-ml-2 invisible',
                }
              : { lineNumberClassName: 'hidden' })}
            lineProps={({ lineNumber, type }) => {
              const lineNumberMap =
                type === 'original' ? leftLineNumberMap : rightLineNumberMap;

              const schemaCoordinatesOnCurrentLineIfExists = lineNumber
                ? lineNumberMap[lineNumber]
                : undefined;

              const splitCoordinates =
                schemaCoordinatesOnCurrentLineIfExists?.map((coordinate) =>
                  splitOutTargetsFromCoordinate(coordinate),
                );

              // If the coordinate is a multi-line description, we should not have an ID for it unless it is
              // the first line of that description
              const lineShouldHaveCoordinateId =
                !splitCoordinates?.some(
                  ({ target }) => target === 'description',
                ) ||
                (schemaCoordinatesOnCurrentLineIfExists &&
                  typeof lineNumber === 'number' &&
                  lineNumber > 1 &&
                  !lineNumberMap[lineNumber - 1]?.some((coordinate) =>
                    schemaCoordinatesOnCurrentLineIfExists.includes(coordinate),
                  ));

              const isSelectedCoordinate =
                canMakeComments &&
                linkedCoordinateName &&
                schemaCoordinatesOnCurrentLineIfExists?.includes(
                  linkedCoordinateName,
                );

              return {
                ...(isMinLengthArray(1, splitCoordinates) &&
                splitCoordinates[0]?.coordinate &&
                lineShouldHaveCoordinateId
                  ? {
                      id: `${getIdFriendlyCoordinateName(
                        splitCoordinates[0].coordinate,
                        splitCoordinates[0].target,
                      )}_${type}`,
                    }
                  : {}),
                className: classnames(
                  'group',
                  isSelectedCoordinate
                    ? 'border-y border-dashed border-yellow bg-yellow-lightest'
                    : 'border-y border-transparent',
                  !isMinLengthArray(1, gutterMarkers) && 'pl-12',
                ),
              };
            }}
          />
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};
